import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostBinding, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

@Component({
  selector: 'app-mogo-dropzone',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mogo-dropzone.component.html',
  styleUrls: ['./mogo-dropzone.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MogoDropzoneComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MogoDropzoneComponent),
      multi: true
    }
  ]
})
export class MogoDropzoneComponent implements ControlValueAccessor, Validator {
  @HostBinding('class.dragover') isDragOver: boolean = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() height: string = '100%'; // Default height
  @Input() width: string = '100%';  // Default width
  @Input() fileTypes: string = 'image/*,application/pdf';  // Default file types
  @Output() fileChange = new EventEmitter<File[]>();
  
  lockdown : boolean = false;
  files: File[] = [];
  invalidFiles: File[] = [];

  private onChange: (files: File[]) => void = (_files: File[]) => { };

  private onTouched: () => void = () => { };
  private onValidatorChange: () => void = () => { };

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDrop(event: DragEvent) {
    if (this.lockdown) {
      return;
    }

    event.preventDefault();
    this.isDragOver = false;
    const files = Array.from(event.dataTransfer?.files || []);
    this.updateFiles(files);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileSelected(event: Event) {
    if (this.lockdown) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    this.updateFiles(files);
  }

  updateFiles(files: File[]) {
    if (files.length > 0) {
      this.files = [...this.files, ...files];
      this.updateInvalidFiles();
      this.onChange(this.files);
      this.fileChange.emit(this.files);
      this.onValidatorChange();
    }
    this.onTouched();
  }

  removeFile(index: number, event: MouseEvent) {
    if(this.lockdown){
      return;
    }

    event.stopPropagation();
    this.files.splice(index, 1);
    this.updateInvalidFiles();
    this.onChange(this.files);
    this.fileChange.emit(this.files);
    this.onValidatorChange();
  }

  writeValue(files: File[] | null | undefined): void {
    this.files = files ?? [];
    this.updateInvalidFiles();
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (files: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    this.updateInvalidFiles();

    if (this.invalidFiles.length === 0) {
      return null;
    }

    return {
      invalidFileTypes: {
        acceptedFileTypes: this.fileTypes,
        invalidFileNames: this.invalidFiles.map((file) => file.name)
      }
    };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.disabled = isDisabled;
    }
    this.lockdown = isDisabled;

  }

  onDropzoneClick() {
    this.fileInput.nativeElement.click();
  }

  isFileInvalid(file: File): boolean {
    return this.invalidFiles.some((invalidFile) => invalidFile === file);
  }

  get invalidFileNames(): string {
    return this.invalidFiles.map((file) => file.name).join(', ');
  }

  private updateInvalidFiles(): void {
    this.invalidFiles = this.files.filter((file) => !this.isAcceptedFile(file));
  }

  private isAcceptedFile(file: File): boolean {
    if (!(file instanceof File)) {
      return true;
    }

    const acceptedTypes = this.fileTypes.split(',')
      .map((fileType) => fileType.trim().toLowerCase())
      .filter((fileType) => fileType.length > 0);

    if (acceptedTypes.length === 0) {
      return true;
    }

    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return acceptedTypes.some((acceptedType) => {
      if (acceptedType.startsWith('.')) {
        return fileName.endsWith(acceptedType);
      }

      if (acceptedType.endsWith('/*')) {
        const acceptedMimePrefix = acceptedType.slice(0, acceptedType.length - 1);
        return mimeType.startsWith(acceptedMimePrefix);
      }

      return mimeType === acceptedType;
    });
  }
}
